import {
  MODEL_PRICING,
  PLAN_CONFIGS,
} from '../../../../../shared-pricing-config'
import type { ModelPricing } from '../../../../../shared-pricing-config'
import type {
  CodeLine,
  CodeSampleTab,
  CodeSegment,
  CodeTone,
} from '@/features/home/data/codeSamples'
import type { FlowStep, ResponseMode } from '@/features/home/data/homeContent'
import { API_BASE_URL } from '@/features/home/data/codeSamples'
import { AGENT_TEAM_ID } from '@/features/home/data/homeContent'

/* ------------------------------------------------------------------ */
/* Example team, rendered on the hero canvas. Model ids are real       */
/* catalog ids; display names come from pricing config.                */
/* ------------------------------------------------------------------ */

export interface PreviewAgent {
  label: string
  modelId: string
  blurb: string
  mode?: ResponseMode
}

export const previewTeam = {
  coordinator: {
    label: 'Coordinator',
    modelId: 'claude-sonnet-4-6',
    blurb: 'Answers every request and decides when to bring in a specialist.',
  },
  specialists: [
    {
      label: 'Senior Architect',
      modelId: 'claude-opus-4-6',
      blurb: 'Architecture, security and performance trade-offs.',
      mode: 'advise',
    },
    {
      label: 'Coder',
      modelId: 'gpt-5.6',
      blurb: 'Complex implementations, tests and refactors.',
      mode: 'advise',
    },
    {
      label: 'Reviewer',
      modelId: 'deepseek-v3.2',
      blurb: 'Security audits, bugs and code quality.',
      mode: 'delegate',
    },
  ],
} satisfies { coordinator: PreviewAgent; specialists: Array<PreviewAgent> }

export function modelDisplayName(id: string): string {
  return (MODEL_PRICING[id] as ModelPricing | undefined)?.displayName ?? id
}

/* ------------------------------------------------------------------ */
/* Roster: the six specialist roles the builder's picker offers.       */
/* Names mirror AGENT_DISPLAY_NAMES in the dashboard.                  */
/* ------------------------------------------------------------------ */

export const specialistRoster = [
  {
    name: 'Senior Architect',
    useFor:
      'Architecture, design patterns, security and performance trade-offs.',
  },
  {
    name: 'Researcher',
    useFor: 'APIs, framework docs and libraries you have not used before.',
  },
  {
    name: 'Coder',
    useFor: 'Complex implementations, algorithms, tests and refactors.',
  },
  {
    name: 'Reviewer',
    useFor: 'Security audits, bug detection and code-quality review.',
  },
  {
    name: 'UI/UX Consultant',
    useFor: 'Interface design, user flows, accessibility and design systems.',
  },
  {
    name: 'Front-end Consultant',
    useFor: 'Component architecture, state, performance and build tooling.',
  },
] as const

/* ------------------------------------------------------------------ */
/* Response modes: wording mirrors RESPONSE_MODE_OPTIONS in the         */
/* dashboard so the marketing claim and the product setting agree.     */
/* ------------------------------------------------------------------ */

export interface ResponseModeCard {
  mode: ResponseMode
  label: string
  summary: string
  detail: string
  example: { prompt: string; outcome: string }
  steps: Array<FlowStep>
}

export const responseModes: Array<ResponseModeCard> = [
  {
    mode: 'advise',
    label: 'Advise',
    summary: 'The coordinator writes the reply.',
    detail:
      "The specialist's answer goes back to the coordinator as advice. The coordinator combines it with everything else it knows and writes the final response.",
    example: {
      prompt: 'Should we shard the orders table now, or wait?',
      outcome:
        'The Senior Architect weighs in. The coordinator writes one answer with the trade-offs in the context of your codebase.',
    },
    steps: [
      { kind: 'request', label: 'Request' },
      { kind: 'coordinator', label: 'Coordinator' },
      { kind: 'specialist', label: 'Senior Architect' },
      {
        kind: 'coordinator',
        label: 'Coordinator',
        via: { label: 'advises', mode: 'advise' },
      },
      { kind: 'reply', label: 'Reply' },
    ],
  },
  {
    mode: 'delegate',
    label: 'Delegate',
    summary: 'The answer goes straight to the user.',
    detail:
      'When the specialist gives a final answer, it is sent to the user exactly as written. The coordinator hands off and adds nothing.',
    example: {
      prompt: 'Review this diff for security issues.',
      outcome:
        "The Reviewer's findings arrive exactly as it wrote them. Nothing is summarised or softened on the way back.",
    },
    steps: [
      { kind: 'request', label: 'Request' },
      { kind: 'coordinator', label: 'Coordinator' },
      { kind: 'specialist', label: 'Reviewer' },
      {
        kind: 'reply',
        label: 'Reply',
        via: { label: 'delegates', mode: 'delegate' },
      },
    ],
  },
]

export const responseModeNotes = [
  'Advise is the default for every specialist.',
  'Only specialists can delegate. The coordinator answers in its own words.',
  'A delegating specialist that needs more context still asks through the coordinator first.',
] as const

/* ------------------------------------------------------------------ */
/* Builder                                                              */
/* ------------------------------------------------------------------ */

/** What the side panel lets you set on any agent node. */
export const agentSettings = [
  'Model, from the full catalog',
  'Fallback model if the first is unavailable',
  'Custom rules, in plain language',
  'Response mode: Advise or Delegate',
] as const

export const callFacts = [
  {
    value: '2',
    label: 'request formats',
    body: 'OpenAI chat completions and Anthropic messages, on the same endpoint.',
  },
  {
    value: 'SSE',
    label: 'streaming',
    body: 'Streams like a single model. A delegated reply arrives as one chunk.',
  },
  {
    value: '$0',
    label: 'team surcharge',
    body: "Each agent bills at its own model's rate for the tokens it uses.",
  },
] as const

const s = (text: string, tone?: CodeTone): CodeSegment => ({ text, tone })
const line = (...segments: Array<CodeSegment>): CodeLine => ({ segments })
const hl = (...segments: Array<CodeSegment>): CodeLine => ({
  segments,
  highlight: true,
})
const blank = line()

/** Same shape as the quickstart tabs; the highlighted line is the team id. */
export const teamCodeTabs: Array<CodeSampleTab> = [
  {
    id: 'python',
    label: 'Python',
    lines: [
      line(
        s('from', 'keyword'),
        s(' openai '),
        s('import', 'keyword'),
        s(' OpenAI'),
      ),
      blank,
      line(
        s('client = OpenAI(api_key='),
        s('"sk_..."', 'string'),
        s(', base_url='),
        s(`"${API_BASE_URL}"`, 'string'),
        s(')'),
      ),
      blank,
      line(s('stream = client.chat.completions.create(')),
      hl(
        s('    model='),
        s(`"${AGENT_TEAM_ID}"`, 'string'),
        s(',  '),
        s('# the team id is the model', 'comment'),
      ),
      line(
        s('    messages=[{'),
        s('"role"', 'string'),
        s(': '),
        s('"user"', 'string'),
        s(', '),
        s('"content"', 'string'),
        s(': '),
        s('"Review this diff"', 'string'),
        s('}],'),
      ),
      line(s('    stream='), s('True', 'keyword'), s(',')),
      line(s(')')),
    ],
  },
  {
    id: 'node',
    label: 'Node.js',
    lines: [
      line(
        s('import', 'keyword'),
        s(' OpenAI '),
        s('from', 'keyword'),
        s(' '),
        s("'openai'", 'string'),
      ),
      blank,
      line(
        s('const', 'keyword'),
        s(' client = '),
        s('new', 'keyword'),
        s(' OpenAI({ apiKey: '),
        s("'sk_...'", 'string'),
        s(', baseURL: '),
        s(`'${API_BASE_URL}'`, 'string'),
        s(' })'),
      ),
      blank,
      line(
        s('const', 'keyword'),
        s(' stream = '),
        s('await', 'keyword'),
        s(' client.chat.completions.create({'),
      ),
      hl(
        s('  model: '),
        s(`'${AGENT_TEAM_ID}'`, 'string'),
        s(',  '),
        s('// the team id is the model', 'comment'),
      ),
      line(
        s('  messages: [{ role: '),
        s("'user'", 'string'),
        s(', content: '),
        s("'Review this diff'", 'string'),
        s(' }],'),
      ),
      line(s('  stream: '), s('true', 'keyword'), s(',')),
      line(s('})')),
    ],
  },
  {
    id: 'curl',
    label: 'curl',
    lines: [
      line(s('curl '), s(`${API_BASE_URL}/chat/completions`, 'string'), s(' \\')),
      line(
        s('  -H ', 'muted'),
        s('"Authorization: Bearer sk_..."', 'string'),
        s(' \\'),
      ),
      line(
        s('  -H ', 'muted'),
        s('"Content-Type: application/json"', 'string'),
        s(' \\'),
      ),
      line(s('  -d ', 'muted'), s("'{", 'string')),
      hl(s(`    "model": "${AGENT_TEAM_ID}",`, 'string')),
      line(
        s(
          '    "messages": [{"role": "user", "content": "Review this diff"}],',
          'string',
        ),
      ),
      line(s('    "stream": true', 'string')),
      line(s("  }'", 'string')),
    ],
  },
]

/* ------------------------------------------------------------------ */
/* FAQ: rendered on the page and emitted as FAQPage JSON-LD.           */
/* ------------------------------------------------------------------ */

const teamsPerPlan = Object.values(PLAN_CONFIGS)
  .map((p) => `${p.name} ${p.stackLimit === -1 ? 'unlimited' : p.stackLimit}`)
  .join(', ')

export const agentsFaqs = [
  {
    question: 'How do I call an agent team?',
    answer:
      "Use the team's id as the model in any chat completions or messages request. The same API key, request format and streaming work exactly as they do for a single model.",
  },
  {
    question: 'What is the difference between Advise and Delegate?',
    answer:
      "Advise sends the specialist's answer back to the coordinator, which combines it with everything else it knows and writes the reply. Delegate sends the specialist's final answer to the user exactly as written. Advise is the default, and only specialists can delegate.",
  },
  {
    question: 'Does the coordinator always consult a specialist?',
    answer:
      'No. It answers routine requests on its own and consults a specialist when a request needs one, up to five consultation rounds per reply.',
  },
  {
    question: 'How is an agent team billed?',
    answer:
      "Each agent is charged at its own model's rate for the tokens it uses, so a reply costs the coordinator's tokens plus any specialist's tokens. There is no extra charge for using a team, and provider tiers apply as usual.",
  },
  {
    question: 'Which specialists can I add?',
    answer:
      'Six roles: Senior Architect, Researcher, Coder, Reviewer, UI/UX Consultant and Front-end Consultant. Each can be added once, with its own model, an optional fallback model and custom rules.',
  },
  {
    question: 'How many teams can I create?',
    answer: `It depends on your plan: ${teamsPerPlan}. Teams are private to your account and each one has an id you choose, which stays fixed after creation.`,
  },
] as const
